import { Tracing } from "@tento-core/tracing";
import { FRAME_BUDGET } from "@tento-ui/frame-budget";
import { FrameMarks } from "@tento-ui/frame-marks";
import { ScrollUtils } from "@tento-ui/scroll-utils";

export interface Scrollable {
  contentOffset: number;
  delegate: Option<Scrollable.Delegate>;

  scrollTo(
    offset: number,
    scrollBehavior: ScrollBehavior,
    useNativeScrollTo: boolean,
    rendered: boolean,
  ): void;

  adjustContentOffset(delta: number): void;
}

export namespace Scrollable {
  export interface Delegate {
    onUserScrolled: () => void;
    /**
     * Every scroll event, the instant it arrives, tagged with whether the USER
     * caused it. A snap or programmatic `scrollTo` moves the viewport too but
     * is the engine's own doing: the motion deadline and a settle waiter's
     * gesture clock must not count it as input. Fired before
     * `onContentOffsetChanged`.
     */
    onScrollActivity: (userInitiated: boolean) => void;
    /**
     * A scroll event that moved only the cross axis (the same element scrolls
     * vertically under a horizontal engine). The content offset is unchanged,
     * so `onScrollActivity` does not fire; a motion-time drain budget still
     * needs to know the user is moving. Optional.
     */
    onCrossAxisScroll?: () => void;
    onContentOffsetChanged: (offset: number) => void;
    /**
     * The furthest content offset this scrollable may present, per direction —
     * `ScrollWindow.minReach` / `maxReach`, which answer `∓Infinity` while
     * there is no limit to state. Read per scroll event, so they must stay
     * cheap; both are a field read plus one map lookup.
     */
    minReach: () => number;
    maxReach: () => number;
  }

  /**
   * Trailing debounce that pays one setTimeout per burst instead of a
   * clear+set per event: events bump a deadline (a field write); the single
   * armed timer re-arms for the remainder when it wakes early. Scroll and
   * wheel streams fire per frame, so the per-event install/clear overhead
   * of the naive shape was itself visible in profiles.
   */
  class TrailingTimer {
    private deadline = 0;
    private timer: Option<ReturnType<typeof setTimeout>> = null;
    constructor(private readonly onFire: () => void) {}

    bump(delayMs: number) {
      this.deadline = performance.now() + delayMs;
      if (this.timer == null) this.arm(delayMs);
    }

    private arm(ms: number) {
      this.timer = setTimeout(() => {
        this.timer = null;
        const remaining = this.deadline - performance.now();
        if (remaining > 5) {
          this.arm(remaining);
          return;
        }
        this.onFire();
      }, ms);
    }

    cancel() {
      if (this.timer != null) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    }

    get pending(): boolean {
      return this.timer != null;
    }
  }

  export class Dom implements Scrollable {
    static widthPx = 16777200;

    contentOffset: number = 0;
    delegate: Option<Scrollable.Delegate>;
    private isProgrammaticScroll = false;
    private lastKnownScroll: number = 0;
    /**
     * Whether the DOM has shown that it holds this scrollable's position — see
     * {@link isLostOrigin}. False until a scroll event reads a non-zero
     * position, and again whenever the host reports the layout box gone
     * ({@link positionLost}).
     */
    private positionHeld = false;
    /**
     * The position read by the most recent scroll notification, or null once
     * this scrollable writes a new position the DOM has not echoed yet.
     */
    #observedPosition: Option<{ left: number; top: number }> = null;
    /** Why `#observedPosition` is null, for the `scroll/observed-miss` trace. */
    #observedMiss: "never-notified" | "moved-self" = "never-notified";
    /** The scroll event this scrollable last notified (its position is `#observedPosition`); null once it moves. */
    #notifiedEvent: Option<Event> = null;

    constructor(
      readonly $root: HTMLElement,
      readonly domDelegate: Optional<Dom.Delegate>,
      readonly scrollDirection: "vertical" | "horizontal" = "horizontal",
      readonly baseOffset = Dom.widthPx / 2,
    ) {
      this.lastKnownScroll = this.baseOffset;
      // Center the DOM scroll position to `baseOffset` synchronously
      // before attaching listeners. The class treats `baseOffset` as
      // the virtual origin (`contentOffset = scrollLeft - baseOffset`),
      // but the DOM's default scrollLeft is 0. Without this, the first
      // browser-fired scroll event reads scrollLeft=0 and computes
      // `contentOffset = -baseOffset` (~-8.4M px), causing the scroll
      // engine to extend its window thousands of cells out and bind
      // year-1558/1559 columns until the engine's first `scrollTo`
      // settles — visible as the year-label flash on initial load.
      if (this.scrollDirection === "horizontal") {
        $root.scrollLeft = this.baseOffset;
      } else {
        $root.scrollTop = this.baseOffset;
      }
      this.initializeListeners($root);
    }

    private initializeListeners($root: HTMLElement) {
      const settleDelayMs = 80;

      let isWheelActive = false;
      let scrollEndFired = false;
      const wheelSettleMs = 120;

      // Interrupt any in-progress snap scroll and clear pending snap timers.
      // Called when the user resumes scrolling (wheel/touch).
      const cancelSnapScroll = () => {
        snapTimer.cancel();
        if (!this.isProgrammaticScroll) return;
        // Stop native smooth scroll by instant-scrolling to current position
        if (this.scrollDirection === "horizontal") {
          $root.scrollTo({ left: $root.scrollLeft, behavior: "instant" });
          this.lastKnownScroll = $root.scrollLeft;
        } else {
          $root.scrollTo({ top: $root.scrollTop, behavior: "instant" });
          this.lastKnownScroll = $root.scrollTop;
        }
        // Cancel ScrollUtils RAF-based animation
        ScrollUtils.cancelScroll($root);
        // Fallback: clear flag if scrollend doesn't fire (non-scrollend browsers)
        if (!hasScrollend) {
          setTimeout(() => { this.isProgrammaticScroll = false; }, 50);
        }
      };

      const onScrollSettle = () => {
        if (this.isProgrammaticScroll) return;
        if (this.domDelegate.snapping?.enabled()) {
          this.domDelegate.snapping.onSnap();
        }
      };

      const snapTimer = new TrailingTimer(onScrollSettle);
      const wheelSettle = new TrailingTimer(() => {
        isWheelActive = false;
        this.domDelegate!.onWheelActivity?.(false);
        maybeSettle();
      });
      const scrollSettle = new TrailingTimer(() => {
        this.delegate?.onUserScrolled?.();
      });

      const scheduleSettle = () => {
        snapTimer.bump(settleDelayMs);
      };

      const maybeSettle = () => {
        if (isWheelActive || !scrollEndFired) return;
        scrollEndFired = false;
        scheduleSettle();
      };

      const hasScrollend = "onscrollend" in $root;

      $root.addEventListener("wheel", () => {
        cancelSnapScroll();
        if (!isWheelActive) this.domDelegate!.onWheelActivity?.(true);
        isWheelActive = true;
        wheelSettle.bump(wheelSettleMs);
      }, { passive: true });

      $root.addEventListener("touchstart", () => {
        cancelSnapScroll();
      }, { passive: true });

      $root.addEventListener("pointerdown", () => {
        cancelSnapScroll();
      }, { passive: true });

      if (hasScrollend) {
        $root.addEventListener("scrollend", () => {
          if (this.isProgrammaticScroll) {
            this.isProgrammaticScroll = false;
            return;
          }
          scrollEndFired = true;
          maybeSettle();
        });
      }

      $root!.addEventListener(
        "scroll",
        (event) => {
          const eventMark = FrameMarks.begin();
          // The scroll event opens the frame: its budget counts from here.
          FRAME_BUDGET.noteInput();
          this.#notifiedEvent = null;
          try {
            onScrollEvent(event);
          } finally {
            FrameMarks.end("scroll.event", eventMark, 0, 0);
          }
        },
        {
          passive: true,
        },
      );
      const onScrollEvent = (event: Event) => {
          snapTimer.cancel();
          scrollEndFired = false;

          const currentScroll = this.scrollDirection === "horizontal"
            ? this.$root.scrollLeft
            : this.$root.scrollTop;
          const scrollDelta = currentScroll - this.lastKnownScroll;
          if (scrollDelta !== 0 && this.isLostOrigin(currentScroll)) {
            this.reassertPosition();
            return;
          }
          if (currentScroll !== 0) this.positionHeld = true;
          this.lastKnownScroll = currentScroll;
          if (scrollDelta === 0) {
            // The same element scrolls vertically; sheets still need its viewport.
            this.delegate?.onCrossAxisScroll?.();
            this.notifyScroll();
            this.#notifiedEvent = event;
            return;
          }

          this.contentOffset = currentScroll - this.baseOffset;

          this.delegate?.onScrollActivity(!this.isProgrammaticScroll);
          this.notifyScroll();
          this.#notifiedEvent = event;
          this.delegate?.onContentOffsetChanged(this.contentOffset);
          this.clampToReach();

          scrollSettle.bump(50);

          if (hasScrollend) return;
          if (this.isProgrammaticScroll) return;
          snapTimer.bump(150);
      };
    }

    /**
     * A read of `scrollLeft === 0` that is the DOM's DEFAULT, not a position.
     *
     * The constructor centres the strip with `scrollLeft = baseOffset`, and
     * every horizontal instant write skips the readback. Both are dropped by an
     * element with no layout box — built under a `display: none` ancestor (the
     * onboarding carousel's off-screen pages), or not yet connected — and a box
     * that is created later starts at 0. The first scroll event on either axis
     * (the viewport scrolls vertically too) then read that 0 as a position
     * `baseOffset` (~8.4M px) left of the model, far past any reach: a preview
     * sheet bound at 1826 with `scroll/coverage-repair/stuck`, because the
     * repair only reconciles and nothing ever moves the view back.
     *
     * Only while the DOM has not yet SHOWN it holds a position: once a scroll
     * event has read a non-zero position, 0 is a real drag to the strip's edge
     * and is adopted and reach-clamped as before (`sheets/strip-edge-stays-on-
     * axis`). Horizontal only: a vertical axis's content height genuinely
     * clamps, so 0 there is a real top.
     */
    private isLostOrigin(currentScroll: number): boolean {
      return (
        this.scrollDirection === "horizontal" &&
        this.baseOffset > 0 &&
        !this.positionHeld &&
        currentScroll === 0
      );
    }

    /**
     * The host saw the layout box go away (disconnect, a zero-size
     * observation): the next box starts at `scrollLeft = 0`, which is not a
     * position this scrollable ever wrote.
     */
    positionLost(): void {
      this.positionHeld = false;
    }

    /**
     * Write the model's position back into the DOM when the DOM may have lost
     * it — the host calls this when a layout box (re)appears, and the scroll
     * listener when it reads a lost origin. A no-op once the position is held,
     * so it never fights a scroll in flight.
     */
    reassertPosition(): void {
      if (this.scrollDirection !== "horizontal" || this.positionHeld) return;
      const target = this.lastKnownScroll;
      const before = this.$root.scrollLeft;
      if (before !== target) this.$root.scrollLeft = target;
      const after = this.$root.scrollLeft;
      // Held only if the write stuck and is distinguishable from the default.
      this.positionHeld = after === target && after !== 0;
      if (before !== target) {
        Tracing.log("scroll/origin-reasserted", {
          from: before,
          to: target,
          held: this.positionHeld,
        });
      }
    }

    /** `offset` pulled onto the addressable range — the pure half of
     *  {@link clampToReach}, shared with {@link scrollTo} so a programmatic move
     *  cannot land somewhere a drag is forbidden to reach. A `±Infinity` bound
     *  (no limit stated) leaves the offset exactly as it was. */
    private reachClamped(offset: number): number {
      const delegate = this.delegate;
      if (delegate == null) return offset;
      return Math.min(Math.max(offset, delegate.minReach()), delegate.maxReach());
    }

    /**
     * Pull the scroll position back onto the addressable range.
     *
     * The strip is a fixed {@link Dom.widthPx} with its origin at the middle,
     * and that width is not a claim about the content — it is what makes a deep
     * scroll cheap (no re-anchor for ten thousand columns). At a week column's
     * ~200px its left edge is ~40,000 columns before the anchor, so a native
     * scrollbar dragged to the end used to present the year 1253: real-looking
     * columns with nothing behind them, and a cell fetch for a date the server
     * answered 500 to (R19/F5). Shrinking the strip was rejected — the reach is
     * per-mapper, the strip is not — so the width stays and the USABLE range
     * shrinks.
     *
     * Ordering is load-bearing: the clamp runs AFTER the delegate has been told
     * about the raw offset, not before. The limit only becomes knowable as a
     * side effect of the reconcile that notification triggers — the window
     * walks toward the requested pixels and stops at the axis edge, which is
     * what makes that edge's offset readable. Clamping first would mean never
     * walking, never learning the edge, and never clamping. The transient is
     * safe on its own account: the window's refusal to walk past the reach
     * means the indices resolved during it are already pulled onto the axis, so
     * the frame in between shows the edge columns, not 1253's.
     *
     * The write-back is announced rather than left to the scroll event it
     * provokes: that event sees a zero delta against `lastKnownScroll` and
     * returns early by design, so the reconcile for the clamped position has to
     * be asked for here.
     */
    private clampToReach(): void {
      const delegate = this.delegate;
      if (delegate == null) return;
      const clamped = this.reachClamped(this.contentOffset);
      if (clamped === this.contentOffset) return;
      const target = clamped + this.baseOffset;
      const adjustMark = FrameMarks.begin();
      if (this.scrollDirection === "horizontal") {
        this.$root.scrollLeft = target;
      } else {
        this.$root.scrollTop = target;
      }
      FrameMarks.end("scroll.adjust", adjustMark, 0, 0);
      this.contentOffset = clamped;
      this.lastKnownScroll = target;
      this.notifyScroll();
      delegate.onContentOffsetChanged(clamped);
    }

    adjustContentOffset(delta: number): void {
      if (delta === 0) return;
      const adjustMark = FrameMarks.begin();
      try {
        this.#adjustContentOffset(delta);
      } finally {
        FrameMarks.end("scroll.adjust", adjustMark, 1, 0);
      }
    }

    #adjustContentOffset(delta: number): void {
      this.#movedSelf();

      if (this.scrollDirection === "vertical") {
        const targetScroll = Math.max(0, this.$root.scrollTop + delta);
        this.lastKnownScroll = targetScroll;
        this.$root.scrollTop = targetScroll;
        this.contentOffset = this.$root.scrollTop - this.baseOffset;
        this.lastKnownScroll = this.$root.scrollTop;
      } else {
        this.lastKnownScroll = this.$root.scrollLeft + delta;
        this.$root.scrollLeft += delta;
        this.contentOffset = this.$root.scrollLeft - this.baseOffset;
        this.lastKnownScroll = this.$root.scrollLeft;
      }
    }

    /** Fresh viewport position without asking layout for content extent. */
    getScrollPosition(): { left: number; top: number } {
      return { left: this.$root.scrollLeft, top: this.$root.scrollTop };
    }

    /**
     * The position this scrollable last read when it notified a scroll, without
     * touching the DOM: a read after the frame's writes would force layout. Null
     * before the first notification. After this scrollable moves itself, the
     * scrolled axis is unknown (`left` NaN, or a null position for a vertical
     * scroller) until the DOM's scroll event reports the move. A null read is
     * traced as `scroll/observed-miss` with its cause.
     */
    observedScrollPosition(): Option<{ left: number; top: number }> {
      const seen = this.#observedPosition;
      if (seen == null) {
        Tracing.log("scroll/observed-miss", { cause: this.#observedMiss, direction: this.scrollDirection });
      }
      return seen;
    }

    /**
     * The position this scrollable read when it notified `event`, the scroll
     * event being dispatched, or null when it did not notify that event (it
     * reasserted a lost origin), or someone else moved it since
     * (`forgetEventPosition`); after a self move only the axis it did not
     * move is observed (`left` NaN for a horizontal one). Its own listener
     * runs first: a later listener of the same event reads this instead of the
     * DOM, because by then the column engine has recycled columns inside the
     * event (attribute and inline-style writes) and a `scrollTop` read would
     * force that layout.
     */
    positionAt(event: Event): Option<{ left: number; top: number }> {
      return event === this.#notifiedEvent ? this.#observedPosition : null;
    }

    /** Someone else moved this scroller (a vertical `scrollTop` write): the event's position no longer stands. */
    forgetEventPosition(): void {
      this.#notifiedEvent = null;
    }

    /**
     * This scrollable is about to move itself. A horizontal move cannot change
     * the vertical position, so the observed `top` stays and `left` becomes
     * unknown (null position) only on the scrolled axis of a vertical scroller.
     */
    #movedSelf(): void {
      const seen = this.#observedPosition;
      this.#observedPosition = seen != null && this.scrollDirection === "horizontal"
        ? { left: Number.NaN, top: seen.top }
        : null;
      if (this.#observedPosition == null) this.#observedMiss = "moved-self";
    }

    private notifyScroll(): void {
      const recipient = this.domDelegate;
      const onScroll = recipient.onScroll;
      const onScrollPosition = recipient.onScrollPosition;
      const info = onScroll ? this.getScrollInfo() : null;
      // Snapshot payloads and ownership before a listener can reenter binding.
      const position = info != null ? { left: info.left, top: info.top } : this.getScrollPosition();
      this.#observedPosition = { left: position.left, top: position.top };
      if (info != null) onScroll?.call(recipient, info);
      if (onScrollPosition != null && this.domDelegate === recipient &&
        recipient.onScrollPosition === onScrollPosition) {
        onScrollPosition?.call(recipient, position);
      }
    }

    getScrollInfo(): Option<{
      left: number;
      top: number;
      scrollHeight: number;
    }> {
      return {
        left: this.$root.scrollLeft,
        top: this.$root.scrollTop,
        scrollHeight: this.$root.scrollHeight,
      };
    }

    private clearProgrammaticScroll() {
      this.isProgrammaticScroll = false;
    }

    scrollTo(
      requestedOffset: number,
      scrollBehavior: ScrollBehavior,
      useNativeScrollTo: boolean,
      rendered: boolean,
    ) {
      // Same limit as a drag, applied before any of the four branches below —
      // an anchor the engine reached by walking the index map (which is NOT
      // reach-bounded: reaching an anchor is all-or-nothing) can still sit off
      // the axis, and moving there would present exactly the columns
      // {@link clampToReach} exists to keep off screen.
      const offset = this.reachClamped(requestedOffset);
      // A new target supersedes the old snap animation, including when this
      // move uses an instant/native write instead of another RAF animation.
      ScrollUtils.cancelScroll(this.$root);
      this.isProgrammaticScroll = true;
      this.#movedSelf();

      if (scrollBehavior === "instant") {
        if (this.scrollDirection === "horizontal") {
          // No readback on the horizontal axis: the content strip is the
          // fixed Dom.widthPx (the baseOffset-centering invariant depends on
          // it), so the write cannot clamp — and reading scrollLeft straight
          // after writing it forces a synchronous layout per programmatic
          // scroll. The vertical axis keeps the readback (real content
          // heights genuinely clamp).
          const target = offset + this.baseOffset;
          this.$root!.scrollLeft = target;
          this.contentOffset = offset;
          this.lastKnownScroll = target;
        } else {
          this.$root!.scrollTop = offset + this.baseOffset;
          this.contentOffset = this.$root!.scrollTop - this.baseOffset;
          this.lastKnownScroll = this.$root!.scrollTop;
        }
        this.clearProgrammaticScroll();
      } else if (!rendered) {
        // 1a) If the index is not rendered
        if (this.scrollDirection === "horizontal") {
          const target = offset + this.baseOffset;
          this.$root!.scrollTo({ left: target, behavior: "instant" });
          this.contentOffset = offset;
          this.lastKnownScroll = target;
        } else {
          this.$root!.scrollTo({
            top: offset + this.baseOffset,
            behavior: "instant",
          });
          this.contentOffset = this.$root!.scrollTop - this.baseOffset;
          this.lastKnownScroll = this.$root!.scrollTop;
        }
        this.clearProgrammaticScroll();
      } else if (useNativeScrollTo || scrollBehavior === "auto") {
        // 1b) If using the default scroll behavior — cleared by consolidated scrollend listener
        if (this.scrollDirection === "horizontal") {
          this.$root!.scrollTo({
            left: offset + this.baseOffset,
            behavior: scrollBehavior ?? "smooth",
          });
        } else {
          this.$root!.scrollTo({
            top: offset + this.baseOffset,
            behavior: scrollBehavior ?? "smooth",
          });
        }
        if (!("onscrollend" in this.$root)) {
          setTimeout(() => this.clearProgrammaticScroll(), 500);
        }
      } else {
        // 1c) Manually scroll using recursive RAF calls
        ScrollUtils.scrollTo(
          this.$root!,
          offset + this.baseOffset,
          300,
          true,
          this.scrollDirection,
        );
        if (!("onscrollend" in this.$root)) {
          setTimeout(() => this.clearProgrammaticScroll(), 500);
        }
      }
    }
  }

  export namespace Dom {
    export interface Delegate {
      /** Synchronous position notification; does not read scrollHeight. */
      onScrollPosition?: Option<(position: { left: number; top: number }) => void>;
      onScroll: (config: {
        left: number;
        top: number;
        scrollHeight: number;
      }) => void;
      snapping: {
        enabled: () => boolean;
        onSnap: () => void;
      };
      /**
       * Fires (true) on the first wheel event of a burst and (false) when the
       * wheel settles (the existing `isWheelActive` transitions — trailing
       * 120ms). Wheel-only by design: touch/pointer drags keep normal
       * hit-testing so pointer capture and drop targeting are unaffected.
       */
      onWheelActivity: (active: boolean) => void;
    }
  }
}
